import type { AxiosError } from 'axios';

import { PWEXUtils } from 'exceptions';
import appStore from 'redux/stores/app.store';
import ChromeExtensionService from 'services/ChromeExtensionService';
import { WEB_PLATFORM_VERSION } from 'shared/infra/constants';
import amplitude from 'tools/amplitude';
import { getCurrentDateTime } from '../../domain';

interface ErrorResponseData {
  msg?: string;
  error?: {
    message?: string;
  };
  [key: string]: unknown;
}

interface LogHttpErrorParams {
  error: unknown;
  traceId: string;
}

/**
 * Tracks HTTP errors using Amplitude ERROR_PWEX event
 * @param error - Axios error object
 */
export function logHttpError({ error, traceId }: LogHttpErrorParams): void {
  try {
    const axiosError = error as AxiosError<ErrorResponseData>;

    const responseConfig = axiosError?.response?.config;
    const httpStatus = axiosError?.response?.status;
    const fullUrl = responseConfig?.url || null;
    const httpMethod = responseConfig?.method || null;

    const endpoint = fullUrl
      ? fullUrl
          .replace(/^https?:\/\/[^/]+/, '')
          .split('?')[0]
          .split('#')[0]
      : null;

    const responseData = axiosError?.response?.data;

    const errorMessage =
      responseData?.msg ||
      responseData?.error?.message ||
      axiosError?.message ||
      undefined;

    const partnerId = appStore.getPartnerId();

    const exFormatted = PWEXUtils.formatEx(error, partnerId);
    const exceptionParam = {
      ...exFormatted,
      params: {
        http_status_code: httpStatus,
        endpoint,
        http_method: httpMethod,
        error_timestamp: getCurrentDateTime(),
        trace_id: traceId,
      },
    };
    amplitude.logException(exceptionParam);
  } catch (ex) {
    console.warn('Failed to track HTTP error:', ex);
  }
}

/**
 * Tracks network disconnection using Amplitude NETWORK_DISCONNECTION event
 * @param offlineDateTime - The date and time the network was disconnected
 */
export async function logNetworkError(offlineDateTime: string): Promise<void> {
  const partnerId = appStore.getPartnerId();
  const hasExtension = !!(await ChromeExtensionService.isAvailable());

  amplitude.logNetworkError(
    partnerId,
    hasExtension,
    WEB_PLATFORM_VERSION,
    offlineDateTime,
  );
}
